import { useEffect, useRef, useState } from 'react';
import type { VueDuBandeau } from '../partage/api/types';
import { useAllerA, useGesteDuTuto } from './useTuto';
import { placeDuBandeau, type PlaceDuBandeau } from './placeDuBandeau';
import { TexteDuTuto } from './TexteDuTuto';
import { repere, trouverLaPremiere } from './zonesDuTuto';
import { useZoneALEcran, type Rectangle } from './zoneALEcran';
import { AnneauTuto } from './AnneauTuto';
import './BandeauTuto.css';

/**
 * Le bandeau du tuto, tickets 31 et 33, repris
 * au ticket 11 de retours-testeurs pour la présentation et les parties de l'Aide.
 *
 * Un geste à la fois, depuis une carte blanche qui flotte **en bas, au centre** : un rond
 * avec le numéro, « La présentation · étape N sur M » (ou « Aide · Lier deux bulles »), le
 * geste en une ou deux phrases, et à droite « Fais-le pour moi », « Passer », « Quitter ». La
 * dernière étape a deux boutons : « Retour à l'Aide » (ou « Ouvrir l'Aide ») et « Terminer »
 * (ou « Plus tard »). Quitter une partie de l'Aide ramène à sa liste, comme la finir par
 * « Retour à l'Aide ». Une barre fine dit où l'on en est. Dans un niveau lancé en entier
 * (ticket 17 de retours-testeurs), la tête dit aussi « Les bases, 3 sur 8 », et la fin d'une
 * partie propose « Partie suivante » ou « Retour à l'Aide ». Il commence par la carte du niveau
 * (ticket 20) : ce qu'il apporte, puis « Suivant ».
 *
 * La zone à toucher est **entourée de l'anneau (`AnneauTuto.tsx`), sans voile** : on doit pouvoir
 * faire le geste. Le bandeau ne couvre ni cette zone, ni la barre « ajouter dans… », ni
 * l'ampoule (`placeDuBandeau.ts`). Il ne pousse rien : la carte
 * garde sa place.
 */
export function BandeauTuto({ vue }: { vue: VueDuBandeau }) {
  const geste = useGesteDuTuto();
  const allerA = useAllerA();
  const zones = vue.zones;
  const cle = `${vue.etape}:${JSON.stringify(zones)}`;
  const suivie = useZoneALEcran(zones.length > 0 ? () => trouverLaPremiere(zones) : null, cle);
  const zone = suivie.trouvee ? suivie.rectangle : null;
  const ref = useRef<HTMLElement>(null);
  const place = usePlaceDuBandeau(ref, zone);

  const occupe = geste.isPending;
  const faire = (quoi: 'passer' | 'faire' | 'quitter' | 'arreter', rouvrir = false) =>
    geste.mutate({ geste: quoi, vue, rouvrir });

  function faisLePourMoi() {
    // Une étape d'écran : y aller, c'est le geste. Le serveur passe à la suite.
    if (vue.sorte === 'ecran' && vue.cible) allerA(vue.cible);
    faire('faire');
  }

  // L'ampoule est ronde : son anneau aussi (la fin de la présentation l'entoure).
  const rond = zones.length === 1 && zones[0]!.cle === 'ampoule';

  return (
    <>
      {zone && <AnneauTuto zone={zone} className={rond ? 'anneau-tuto--rond' : undefined} />}

      <aside
        ref={ref}
        className="tuto"
        style={
          place
            ? { left: place.left, width: place.largeur, bottom: place.bas ?? undefined, top: place.haut ?? undefined }
            : { visibility: 'hidden' }
        }
        aria-label="Aide"
        aria-live="polite"
      >
        <span className="tuto-numero" aria-hidden="true">
          {vue.carte ? vue.carte.numero : vue.numero}
        </span>

        <div className="tuto-texte">
          {vue.carte ? (
            // La carte d'un niveau lancé en entier (ticket 20) : le niveau, et combien de parties suivent.
            <p className="tuto-quoi">
              Aide · <b>{vue.partie}</b>&nbsp;· {vue.carte.parties} parties
            </p>
          ) : (
            <p className="tuto-quoi">
              {vue.deLAide && 'Aide · '}
              {vue.niveau && `${vue.niveau.nom}, ${vue.niveau.numero} sur ${vue.niveau.total} · `}
              <b>{vue.partie}</b>&nbsp;· étape {vue.numero} sur {vue.total}
            </p>
          )}
          <p className="tuto-geste">
            <TexteDuTuto morceaux={vue.morceaux} />
          </p>
          {geste.isError && <em className="tuto-erreur">Ça n’a pas marché, réessaie</em>}
        </div>

        <div className="tuto-boutons">
          {vue.fin?.suivante ? (
            // Un niveau lancé en entier (ticket 17) : la partie d'après, ou l'arrêter là.
            <>
              <button type="button" className="bouton" onClick={() => faire('passer')} disabled={occupe}>
                {vue.fin.suivante}
              </button>
              <button type="button" className="tuto-lien" onClick={() => faire('arreter', true)} disabled={occupe}>
                {vue.fin.aide}
              </button>
            </>
          ) : vue.fin ? (
            <>
              <button type="button" className="bouton" onClick={() => faire('passer', true)} disabled={occupe}>
                {vue.fin.aide}
              </button>
              <button type="button" className="tuto-lien" onClick={() => faire('passer')} disabled={occupe}>
                {vue.fin.autre}
              </button>
            </>
          ) : vue.sorte === 'lu' ? (
            // Une lecture en cours de partie (ticket 19 : « Supprimer » montré, pas cliqué) :
            // rien à faire à l'écran, « Suivant » la termine, comme dans la visite.
            <>
              <button type="button" className="bouton" onClick={() => faire('passer')} disabled={occupe}>
                Suivant
              </button>
              <button
                type="button"
                className="tuto-lien tuto-quitter"
                onClick={() => faire('quitter', vue.deLAide)}
                disabled={occupe}
              >
                Quitter
              </button>
            </>
          ) : (
            <>
              {vue.pourMoi && (
                <button type="button" className="tuto-lien tuto-lien--fort" onClick={faisLePourMoi} disabled={occupe}>
                  Fais-le pour moi
                </button>
              )}
              <button type="button" className="tuto-lien" onClick={() => faire('passer')} disabled={occupe}>
                Passer
              </button>
              <button
                type="button"
                className="tuto-lien tuto-quitter"
                onClick={() => faire('quitter', vue.deLAide)}
                disabled={occupe}
              >
                Quitter
              </button>
            </>
          )}
        </div>

        <span className="tuto-barre" aria-hidden="true">
          <i style={{ width: vue.carte ? 0 : `${(vue.numero / vue.total) * 100}%` }} />
        </span>
      </aside>
    </>
  );
}

function rectangleDe(el: Element | null): Rectangle | null {
  if (!el) return null;
  const b = el.getBoundingClientRect();
  if (b.width === 0 || b.height === 0) return null;
  return { x: b.left, y: b.top, largeur: b.width, hauteur: b.height };
}

/**
 * Relire, image par image, ce qui entoure le bandeau (une fiche qui s'ouvre, la page qui
 * défile) et le reposer. Le calcul est dans `placeDuBandeau.ts`.
 */
function usePlaceDuBandeau(ref: React.RefObject<HTMLElement | null>, zone: Rectangle | null): PlaceDuBandeau | null {
  const [place, setPlace] = useState<PlaceDuBandeau | null>(null);
  const zoneIci = useRef(zone);
  zoneIci.current = zone;

  useEffect(() => {
    let image = 0;
    let derniere = '';
    const poser = () => {
      const el = ref.current;
      if (el) {
        const suite = placeDuBandeau({
          fenetre: { largeur: window.innerWidth, hauteur: window.innerHeight },
          hauteur: el.offsetHeight,
          fiche: rectangleDe(document.querySelector(repere('fiche'))),
          // L'ampoule de l'Aide aussi : sur un écran étroit, le bandeau se pose au-dessus d'elle.
          // Par leurs repères, comme les zones (ticket 00 de telephone).
          enBas: [repere('feuille-barre'), repere('ampoule')]
            .map((s) => rectangleDe(document.querySelector(s)))
            .filter((r): r is Rectangle => r !== null),
          zone: zoneIci.current,
        });
        const cle = JSON.stringify(suite);
        if (cle !== derniere) {
          derniere = cle;
          setPlace(suite);
        }
      }
      image = requestAnimationFrame(poser);
    };
    image = requestAnimationFrame(poser);
    return () => cancelAnimationFrame(image);
  }, [ref]);

  return place;
}
